import isMatched from './isMatched';
import type DxfArrayScanner from '../DxfArrayScanner';
import { ScannerGroup } from '../DxfArrayScanner';
import parsePoint from './parsePoint';

export const Abort = Symbol('Abort');

export interface DXFParserSnippet {
    code: number | number[];
    name?: string;
    parser?(curr: ScannerGroup, scanner: DxfArrayScanner, entity: any): any;
    isMultiple?: boolean;
    pushContext?: boolean;
}

export type DXFParser = (
    curr: ScannerGroup,
    scanner: DxfArrayScanner,
    target: any,
) => boolean;

function getObjectByPath(target: any, path: string): [any, string] {
    const fragments = path.split('.');

    return [
        fragments.slice(0, -1).reduce((acc, curr) => {
            if (!Object.hasOwn(acc, curr)) {
                acc[curr] = {};
            }
            return acc[curr];
        }, target),
        fragments.at(-1)!,
    ];
}

function createSnippetMaps(snippets: DXFParserSnippet[]) {
    return snippets.reduce((acc, snippet) => {
        if (snippet.pushContext) {
            acc.push({});
        }

        const context = acc[acc.length - 1];
        const codes = Array.isArray(snippet.code) ? snippet.code : [snippet.code];

        codes.forEach((code) => {
            const bin = context[code] || (context[code] = []);

            if (snippet.isMultiple && bin.length) {
                console.warn(
                    `Snippet ${bin.at(-1)!.name} for code(${code}) is shadowed by ${snippet.name}`,
                );
            }

            bin.push(snippet);
        });

        return acc;
    }, [{}] as Record<number, DXFParserSnippet[]>[]);
}

function findSnippetMap(
    snippetMaps: Record<number, DXFParserSnippet[]>[],
    code: number,
    contextIndex: number,
) {
    return snippetMaps.find(
        (map, index) => index >= contextIndex && map[code]?.length,
    );
}

export function createParser(
    snippets: DXFParserSnippet[],
    defaultObject?: any,
): DXFParser {
    return (initialCurr, scanner, target) => {
        const snippetMaps = createSnippetMaps(snippets);
        let isReadOnce = false;
        let contextIndex = snippetMaps.length - 1;
        let curr = initialCurr;

        while (!isMatched(curr, 0, 'EOF')) {
            const snippetMap = findSnippetMap(snippetMaps, curr.code, contextIndex);
            const snippet = snippetMap?.[curr.code].at(-1);

            if (!snippetMap || !snippet) {
                scanner.rewind();
                break;
            }

            if (!snippet.isMultiple) {
                snippetMap[curr.code].pop();
            }

            const { name, parser, isMultiple } = snippet;
            const parsedValue = parser?.(curr, scanner, target);

            if (parsedValue === Abort) {
                scanner.rewind();
                break;
            }

            if (name) {
                const [leaf, fieldName] = getObjectByPath(target, name);

                if (isMultiple) {
                    if (!Object.hasOwn(leaf, fieldName)) {
                        leaf[fieldName] = [];
                    }
                    leaf[fieldName].push(parsedValue);
                } else {
                    leaf[fieldName] = parsedValue;
                }
            }

            if (snippet.pushContext) {
                contextIndex -= 1;
            }

            isReadOnce = true;
            curr = scanner.next();
        }

        if (defaultObject) {
            Object.setPrototypeOf(target, defaultObject);
        }

        return isReadOnce;
    };
}

export function Identity({ value }: ScannerGroup) {
    return value;
}

export function PointParser(_: any, scanner: DxfArrayScanner) {
    return parsePoint(scanner);
}

export function ToBoolean({ value }: ScannerGroup) {
    return !!value;
}
